/**
 * Pinch-to-zoom for in-world duels — a zoom-tuning affordance (owner
 * ask 2026-08-03: "for the zoom just let me pinch… give a readout for
 * the number too so i can convey it").
 *
 * Two-finger pinch anywhere on #game (touches starting on battle
 * buttons are ignored) scales the duel camera live around the fixed
 * midpoint, 1×–6× over the world zoom. Ctrl/trackpad wheel does the
 * same on desktop. The applied multiplier persists to localStorage
 * ('poke-duel-zoom') so the next duel opens at the tuned value, and a
 * small DM Mono pill reads out the number to bake into
 * DUEL_ZOOM_FACTOR once a winner emerges.
 *
 * TESTING-PHASE SURFACE: remove (or gate) alongside the other
 * share-gate flips once the zoom is settled.
 */
import type { DuelStage } from './duel';

export interface DuelPinchHandle {
  destroy(): void;
}

const STORAGE_KEY = 'poke-duel-zoom';

export function mountDuelPinch(stage: DuelStage): DuelPinchHandle {
  const gameEl = document.getElementById('game');
  if (!gameEl) return { destroy: () => {} };

  const pill = document.createElement('div');
  pill.style.cssText = [
    'position:absolute',
    'top:8px',
    'left:50%',
    'transform:translateX(-50%)',
    'z-index:40',
    'padding:3px 10px',
    'border-radius:4px',
    'background:rgba(20,22,28,0.55)',
    'box-shadow:inset 0 1px 0 rgba(255,255,255,0.12)',
    "font-family:'DM Mono',ui-monospace,monospace",
    'font-size:11px',
    'color:rgba(255,255,255,0.68)',
    'pointer-events:none',
    'user-select:none',
  ].join(';');
  gameEl.appendChild(pill);

  const readout = (): void => {
    pill.textContent = `zoom ${stage.getZoomMultiplier().toFixed(2)}×`;
  };
  readout();

  const persist = (applied: number): void => {
    try {
      localStorage.setItem(STORAGE_KEY, applied.toFixed(2));
    } catch {
      // storage unavailable — pinch still works this battle
    }
    readout();
  };
  const apply = (mult: number): void => {
    persist(stage.setZoomMultiplier(mult));
  };

  let pinchStartDist = 0;
  let pinchStartMult = 1;

  const touchDist = (t: TouchList): number =>
    Math.hypot(t[0]!.clientX - t[1]!.clientX, t[0]!.clientY - t[1]!.clientY);

  const onTouchStart = (e: TouchEvent): void => {
    if (e.touches.length !== 2) return;
    const target = e.target as HTMLElement | null;
    if (target?.closest('.battle-actions')) return;
    pinchStartDist = touchDist(e.touches);
    pinchStartMult = stage.getZoomMultiplier();
  };
  const onTouchMove = (e: TouchEvent): void => {
    if (e.touches.length !== 2 || pinchStartDist === 0) return;
    e.preventDefault();
    apply(pinchStartMult * (touchDist(e.touches) / pinchStartDist));
  };
  const onTouchEnd = (e: TouchEvent): void => {
    if (e.touches.length < 2) pinchStartDist = 0;
  };
  const onWheel = (e: WheelEvent): void => {
    // Trackpad pinch reports as ctrl+wheel; plain wheel also accepted
    // (desktop tuning convenience). Steps one integer-zoom detent per
    // tick — a ratio step can't escape a snapped stop at small bases.
    e.preventDefault();
    persist(stage.stepZoom(e.deltaY < 0 ? 1 : -1));
  };

  gameEl.addEventListener('touchstart', onTouchStart, { passive: true });
  gameEl.addEventListener('touchmove', onTouchMove, { passive: false });
  gameEl.addEventListener('touchend', onTouchEnd, { passive: true });
  gameEl.addEventListener('wheel', onWheel, { passive: false });

  return {
    destroy(): void {
      gameEl.removeEventListener('touchstart', onTouchStart);
      gameEl.removeEventListener('touchmove', onTouchMove);
      gameEl.removeEventListener('touchend', onTouchEnd);
      gameEl.removeEventListener('wheel', onWheel);
      pill.remove();
    },
  };
}
